/**
 * React 19 removed `ReactDOM.findDOMNode`, but the editor still depends on libraries that call it:
 * @react-page/core 0.16 (drag and drop hover positioning, cell focus) and the react-draggable
 * version used by its cell resizing. Restore it with the same lookup React used internally:
 * resolve the instance's current fiber and return the first host node beneath it.
 *
 * Remove this once the editor moves to a @react-page release that supports React 19.
 */
import * as ReactDOM from 'react-dom'

// Fiber tags for nodes that own a DOM node: HostComponent, HostText, HostHoistable, HostSingleton
const HOST_TAGS = [5, 6, 26, 27]
const HOST_ROOT = 3

const isCurrentFiber = fiber => {
    let node = fiber
    while (node.return) {
        node = node.return
    }
    return node.tag === HOST_ROOT && node.stateNode && node.stateNode.current === node
}

const findHostFiber = fiber => {
    if (HOST_TAGS.includes(fiber.tag)) {
        return fiber
    }
    for (let child = fiber.child; child !== null; child = child.sibling) {
        const host = findHostFiber(child)
        if (host) {
            return host
        }
    }
    return null
}

export const findDOMNode = instance => {
    if (instance == null) {
        return null
    }
    if (instance.nodeType === 1 || instance.nodeType === 3) {
        return instance
    }

    const fiber = instance._reactInternals
    if (!fiber) {
        throw new Error('findDOMNode: argument is not a mounted component')
    }

    const current = isCurrentFiber(fiber) ? fiber : fiber.alternate || fiber
    const host = findHostFiber(current)

    return host ? host.stateNode : null
}

// `react-dom` is CommonJS, so libraries read `findDOMNode` off its exports object at call time
const reactDOM = ReactDOM.default || ReactDOM

if (typeof reactDOM.findDOMNode !== 'function') {
    try {
        reactDOM.findDOMNode = findDOMNode
    } catch (e) {
        console.error(
            'Unable to install the findDOMNode polyfill required by the content editor',
            e
        )
    }
}
